// =============================================================================
// ClubProfile.jsx - Branding & Appearance Settings Component (PREMIUM ONLY)
// =============================================================================
// Extracted from Settings.jsx to manage club branding including:
// - Club logo upload/removal
// - Sponsor logo upload/removal  
// - Primary/secondary color customization
// - Sponsor URL management
// REQUIRES: Premium subscription tier for access
// =============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { ColorPicker } from '../ColorPicker';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import PremiumFeatureModal from '../PremiumFeatureModal';
import TrialExpiredModal from '../TrialExpiredModal';

// Import configuration constants
import {
  BRANDING_CONFIG
} from '../../utils/settings/settingsConfig';
import { storage, db } from '../../utils/firebase';

// =============================================================================
// VALIDATION FUNCTION: Image File
// =============================================================================
const validateImageFile = (file) => {
  if (!BRANDING_CONFIG.ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Invalid file type. Please upload a JPEG, PNG, or SVG image.');
  }
  
  if (file.size > BRANDING_CONFIG.MAX_LOGO_SIZE_BYTES) {
    throw new Error(`File size exceeds ${BRANDING_CONFIG.MAX_LOGO_SIZE_MB}MB. Please upload a smaller image.`);
  }
  
  return true;
};

// =============================================================================
// CLUBPROFILE COMPONENT
// =============================================================================
const ClubProfile = ({
  clubInfo,
  branding,
  onBrandingChange,
  onSave,
  showSuccess,
  isAdmin = true
}) => {
  // ===========================================================================
  // STATE
  // ===========================================================================
  const [uploadingClubLogo, setUploadingClubLogo] = useState(false);
  const [uploadingSponsorLogo, setUploadingSponsorLogo] = useState(false);
  const [errors, setErrors] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Premium subscription state
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  
  // Trial expired modal state
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  
  // ===========================================================================
  // REFS
  // ===========================================================================
  const clubLogoInputRef = useRef(null);
  const sponsorLogoInputRef = useRef(null);

  // ===========================================================================
  // CHECK PREMIUM STATUS
  // ===========================================================================
  const isPremium = subscriptionTier === 'premium';

  // ===========================================================================
  // LOAD SUBSCRIPTION TIER
  // ===========================================================================
  useEffect(() => {
    const loadSubscriptionTier = async () => {
      if (!clubInfo?.clubId) {
        setIsLoadingSubscription(false);
        return;
      }

      try {
        console.log('🔍 [ClubProfile] Loading subscription tier for club:', clubInfo.clubId);
        const clubDocRef = doc(db, 'clubs', clubInfo.clubId);
        const clubDoc = await getDoc(clubDocRef);
        
        if (clubDoc.exists()) {
          const clubData = clubDoc.data();
          const tier = clubData.subscriptionTier || 'free';
          console.log('📊 [ClubProfile] Subscription tier loaded:', tier);
          setSubscriptionTier(tier);
        } else {
          console.log('⚠️ [ClubProfile] Club document not found, defaulting to free tier');
          setSubscriptionTier('free');
        }
      } catch (error) {
        console.error('❌ [ClubProfile] Error loading subscription tier:', error);
        setSubscriptionTier('free');
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    loadSubscriptionTier();
  }, [clubInfo?.clubId]);
  
  // ===========================================================================
  // TRIAL CHECK
  // ===========================================================================
  const isTrialExpired = () => {
    if (!clubInfo) return false;
    
    const now = new Date();
    const trialEndsAt = clubInfo.trialEndsAt?.toDate ? 
      clubInfo.trialEndsAt.toDate() : 
      new Date(clubInfo.trialEndsAt);
    
    const subscriptionStatus = clubInfo.subscriptionStatus || 'trial';
    const msRemaining = trialEndsAt - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    
    return subscriptionStatus === 'trial' && daysRemaining <= 0;
  };
  
  // ===========================================================================
  // HANDLERS: Expand Toggle with Premium Check and Trial Check
  // ===========================================================================
  const handleExpandToggle = () => {
    // First check if trial is expired
    if (isTrialExpired()) {
      console.log('🚫 [ClubProfile] Trial expired - blocking branding access');
      setShowTrialExpiredModal(true);
      return;
    }
    
    // Then check premium status
    if (!isPremium && !isExpanded) {
      console.log('🚫 [ClubProfile] Non-premium user attempting to access branding');
      setShowPaywallModal(true);
      return;
    }
    setIsExpanded(!isExpanded);
  };
  
  // ===========================================================================
  // HANDLERS: Club Logo Upload
  // ===========================================================================
  const handleClubLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      validateImageFile(file);
      setUploadingClubLogo(true);
      setErrors(prev => ({ ...prev, clubLogo: '' }));
      
      // Create storage reference with timestamp
      const fileName = `club-logos/${clubInfo.clubId}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      // Upload file to Firebase Storage
      await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Delete old logo from storage if it exists
      if (branding && branding.clubLogoUrl) {
        try {
          const oldRef = ref(storage, branding.clubLogoUrl);
          await deleteObject(oldRef);
        } catch (err) {
          console.log('Old logo deletion failed (may not exist):', err);
        }
      }
      
      // Update branding with new logo URL
      const updatedBranding = { ...(branding || {}), clubLogoUrl: downloadURL };
      onBrandingChange(updatedBranding);
      await onSave(updatedBranding);
      
      showSuccess('Club logo uploaded successfully');
      
    } catch (error) {
      console.error('Error uploading club logo:', error);
      setErrors(prev => ({ ...prev, clubLogo: error.message }));
    } finally {
      setUploadingClubLogo(false);
      if (clubLogoInputRef.current) {
        clubLogoInputRef.current.value = '';
      }
    }
  };
  
  // ===========================================================================
  // HANDLERS: Club Logo Removal
  // ===========================================================================
  const handleRemoveClubLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the club logo?')) {
      return;
    }
    
    try {
      setUploadingClubLogo(true);
      
      // Delete from Firebase Storage if exists
      if (branding && branding.clubLogoUrl) {
        try {
          const oldRef = ref(storage, branding.clubLogoUrl);
          await deleteObject(oldRef);
        } catch (err) {
          console.log('Logo deletion failed (may not exist):', err);
        }
      }
      
      // Update branding with null logo
      const updatedBranding = { ...(branding || {}), clubLogoUrl: null };
      onBrandingChange(updatedBranding);
      await onSave(updatedBranding);
      
      showSuccess('Club logo removed successfully');
      
    } catch (error) {
      console.error('Error removing club logo:', error);
      setErrors(prev => ({ ...prev, clubLogo: error.message }));
    } finally {
      setUploadingClubLogo(false);
    }
  };
  
  // ===========================================================================
  // HANDLERS: Sponsor Logo Upload
  // ===========================================================================
  const handleSponsorLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      validateImageFile(file);
      setUploadingSponsorLogo(true);
      setErrors(prev => ({ ...prev, sponsorLogo: '' }));
      
      // Create storage reference with timestamp
      const fileName = `sponsor-logos/${clubInfo.clubId}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      // Upload file to Firebase Storage
      await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Delete old logo from storage if it exists
      if (branding && branding.sponsorLogoUrl) {
        try {
          const oldRef = ref(storage, branding.sponsorLogoUrl);
          await deleteObject(oldRef);
        } catch (err) {
          console.log('Old sponsor logo deletion failed (may not exist):', err);
        }
      }
      
      // Update branding with new sponsor logo URL
      const updatedBranding = { ...(branding || {}), sponsorLogoUrl: downloadURL };
      onBrandingChange(updatedBranding);
      await onSave(updatedBranding);
      
      showSuccess('Sponsor logo uploaded successfully');
      
    } catch (error) {
      console.error('Error uploading sponsor logo:', error);
      setErrors(prev => ({ ...prev, sponsorLogo: error.message }));
    } finally {
      setUploadingSponsorLogo(false);
      if (sponsorLogoInputRef.current) {
        sponsorLogoInputRef.current.value = '';
      }
    }
  };
  
  // ===========================================================================
  // HANDLERS: Sponsor Logo Removal
  // ===========================================================================
  const handleRemoveSponsorLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the sponsor logo?')) {
      return;
    }
    
    try {
      setUploadingSponsorLogo(true);
      
      // Delete from Firebase Storage if exists
      if (branding && branding.sponsorLogoUrl) {
        try {
          const oldRef = ref(storage, branding.sponsorLogoUrl);
          await deleteObject(oldRef);
        } catch (err) {
          console.log('Sponsor logo deletion failed (may not exist):', err);
        }
      }
      
      // Update branding with null sponsor logo
      const updatedBranding = { ...(branding || {}), sponsorLogoUrl: null };
      onBrandingChange(updatedBranding);
      await onSave(updatedBranding);
      
      showSuccess('Sponsor logo removed successfully');
      
    } catch (error) {
      console.error('Error removing sponsor logo:', error);
      setErrors(prev => ({ ...prev, sponsorLogo: error.message }));
    } finally {
      setUploadingSponsorLogo(false);
    }
  };
  
  // ===========================================================================
  // HANDLERS: Color Changes
  // ===========================================================================
  const handleColorChange = async (colorType, newColor) => {
    const updatedBranding = { ...(branding || {}), [colorType]: newColor };
    onBrandingChange(updatedBranding);
    await onSave(updatedBranding);
    showSuccess('Color updated successfully');
  };
  
  // ===========================================================================
  // HANDLERS: Sponsor URL Changes
  // ===========================================================================
  const handleSponsorUrlChange = async (newUrl) => {
    const updatedBranding = { ...(branding || {}), sponsorUrl: newUrl };
    onBrandingChange(updatedBranding);
    await onSave(updatedBranding);
    showSuccess('Sponsor URL updated successfully');
  };

  // ===========================================================================
  // RENDER: Loading State
  // ===========================================================================
  if (isLoadingSubscription) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{ fontSize: '14px', color: '#6b7280' }}>
            Loading branding settings...
          </span>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  // ===========================================================================
  // RENDER: Main Component
  // ===========================================================================
  return (
    <>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb'
      }}>
        {/* =================================================================
            HEADER WITH PREMIUM BADGE
            ================================================================= */}
        <div
          onClick={handleExpandToggle}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '8px',
            margin: '-8px',
            borderRadius: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
              Branding & Appearance
            </h2>
            
            {/* Premium Badge */}
            <div style={{
              background: isPremium 
                ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
                : 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)',
              padding: '4px 12px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontSize: '12px' }}>👑</span>
              <span style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'white'
              }}>
                {isPremium ? 'PREMIUM' : 'PREMIUM ONLY'}
              </span>
            </div>
          </div>

          <div style={{
            fontSize: '20px',
            color: '#6b7280',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
          }}>
            ▼
          </div>
        </div>

        <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
          {isPremium
            ? 'Customize your club\'s logo, colors, and sponsor branding'
            : 'Upgrade to Premium to customize your club\'s appearance with logos and colors'
          }
        </p>

        {/* =================================================================
            EXPANDED CONTENT (PREMIUM ONLY)
            ================================================================= */}
        {isExpanded && isPremium && (
          <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* =================================================================
                CLUB LOGO SECTION
                ================================================================= */}
            <div style={{
              padding: '20px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>🏆</span> Club Logo
              </h3>
              
              <p style={{
                fontSize: '13px',
                color: '#6b7280',
                marginBottom: '12px'
              }}>
                Upload your club logo to display in the header of all shared allocation pages. Recommended size: 200x80px. Max file size: 5MB.
              </p>
              
              {/* Club Logo Preview */}
              {branding && branding.clubLogoUrl && (
                <div style={{
                  marginBottom: '12px',
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px'
                }}>
                  <img 
                    src={branding?.clubLogoUrl}
                    alt="Club Logo Preview"
                    style={{
                      maxHeight: '60px',
                      maxWidth: '180px',
                      objectFit: 'contain'
                    }}
                  />
                  <button
                    onClick={handleRemoveClubLogo}
                    disabled={uploadingClubLogo}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: uploadingClubLogo ? '#9ca3af' : '#fee2e2',
                      color: uploadingClubLogo ? '#6b7280' : '#dc2626',
                      border: uploadingClubLogo ? 'none' : '1px solid #fecaca',
                      borderRadius: '4px',
                      cursor: uploadingClubLogo ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {uploadingClubLogo ? 'Removing...' : 'Remove Logo'}
                  </button>
                </div>
              )}
              
              {/* Club Logo Upload Button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <input
                  ref={clubLogoInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/svg+xml"
                  onChange={handleClubLogoUpload}
                  disabled={uploadingClubLogo || !isAdmin}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => clubLogoInputRef.current?.click()}
                  disabled={uploadingClubLogo || !isAdmin}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: uploadingClubLogo || !isAdmin ? '#9ca3af' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: uploadingClubLogo || !isAdmin ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: uploadingClubLogo || !isAdmin ? 0.6 : 1
                  }}
                >
                  {uploadingClubLogo ? 'Uploading...' : (branding?.clubLogoUrl ? 'Replace Logo' : 'Upload Logo')}
                </button>
                {!isAdmin && (
                  <span style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                    Admin access required
                  </span>
                )}
              </div>
              
              {/* Club Logo Error */}
              {errors.clubLogo && (
                <div style={{
                  marginTop: '12px',
                  fontSize: '13px',
                  color: '#dc2626'
                }}>
                  {errors.clubLogo}
                </div>
              )}
            </div>
            
            {/* =================================================================
                COLOR CUSTOMIZATION SECTION
                ================================================================= */}
            <div style={{
              padding: '20px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>🎨</span> Header Colors
              </h3>
              
              <p style={{
                fontSize: '13px',
                color: '#6b7280',
                marginBottom: '16px'
              }}>
                Choose primary and secondary colors for the header gradient on shared allocation pages.
              </p>
              
              {/* Color Pickers Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Primary Color
                  </label>
                  <ColorPicker
                    color={branding?.primaryColor || '#10b981'}
                    onChange={(color) => handleColorChange('primaryColor', color)}
                    disabled={!isAdmin}
                  />
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Secondary Color
                  </label>
                  <ColorPicker
                    color={branding?.secondaryColor || '#3b82f6'}
                    onChange={(color) => handleColorChange('secondaryColor', color)}
                    disabled={!isAdmin}
                  />
                </div>
              </div>
              
              {/* Color Preview */}
              <div style={{
                marginTop: '16px',
                padding: '20px',
                background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
                borderRadius: '8px',
                color: 'white',
                textAlign: 'center',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Header Preview
              </div>

              {!isAdmin && (
                <p style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic', marginTop: '12px', marginBottom: 0 }}>
                  Admin access required to change colors
                </p>
              )}
            </div>
            
            {/* =================================================================
                SPONSOR LOGO SECTION
                ================================================================= */}
            <div style={{
              padding: '20px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>🤝</span> Sponsor Logo & Link
              </h3>
              
              <p style={{
                fontSize: '13px',
                color: '#6b7280',
                marginBottom: '12px'
              }}>
                Upload your sponsor's logo and add a clickable link. This will be displayed in the top-right of shared allocation pages. Recommended size: 150x60px. Max file size: 5MB.
              </p>
              
              {/* Sponsor Logo Preview */}
              {branding && branding.sponsorLogoUrl && (
                <div style={{
                  marginBottom: '12px',
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px'
                }}>
                  <img 
                    src={branding?.sponsorLogoUrl}
                    alt="Sponsor Logo Preview"
                    style={{
                      maxHeight: '50px',
                      maxWidth: '120px',
                      objectFit: 'contain'
                    }}
                  />
                  <button
                    onClick={handleRemoveSponsorLogo}
                    disabled={uploadingSponsorLogo || !isAdmin}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: uploadingSponsorLogo || !isAdmin ? '#9ca3af' : '#fee2e2',
                      color: uploadingSponsorLogo || !isAdmin ? '#6b7280' : '#dc2626',
                      border: uploadingSponsorLogo || !isAdmin ? 'none' : '1px solid #fecaca',
                      borderRadius: '4px',
                      cursor: uploadingSponsorLogo || !isAdmin ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {uploadingSponsorLogo ? 'Removing...' : 'Remove Logo'}
                  </button>
                </div>
              )}
              
              {/* Sponsor Logo Upload Button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <input
                  ref={sponsorLogoInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/svg+xml"
                  onChange={handleSponsorLogoUpload}
                  disabled={uploadingSponsorLogo || !isAdmin}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => sponsorLogoInputRef.current?.click()}
                  disabled={uploadingSponsorLogo || !isAdmin}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: uploadingSponsorLogo || !isAdmin ? '#9ca3af' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: uploadingSponsorLogo || !isAdmin ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: uploadingSponsorLogo || !isAdmin ? 0.6 : 1
                  }}
                >
                  {uploadingSponsorLogo ? 'Uploading...' : (branding?.sponsorLogoUrl ? 'Replace Logo' : 'Upload Logo')}
                </button>
                {!isAdmin && (
                  <span style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                    Admin access required
                  </span>
                )}
              </div>
              
              {/* Sponsor Logo Error */}
              {errors.sponsorLogo && (
                <div style={{
                  marginBottom: '12px',
                  fontSize: '13px',
                  color: '#dc2626'
                }}>
                  {errors.sponsorLogo}
                </div>
              )}
              
              {/* Sponsor URL Input */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Sponsor Website URL (Optional)
                </label>
                <input
                  type="url"
                  value={branding?.sponsorUrl || ''}
                  onChange={(e) => handleSponsorUrlChange(e.target.value)}
                  placeholder="https://www.sponsor-website.com"
                  disabled={!isAdmin}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: !isAdmin ? '#f3f4f6' : 'white',
                    cursor: !isAdmin ? 'not-allowed' : 'text'
                  }}
                />
                <p style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '4px',
                  fontStyle: 'italic'
                }}>
                  If provided, the sponsor logo will be clickable and link to this URL
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Premium Paywall Modal */}
      <PremiumFeatureModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        featureName="Branding & Appearance"
        currentTier={subscriptionTier}
        requiredTiers={['premium']}
        benefits={[
          "Upload and display your club logo on all shared pages",
          "Choose custom primary and secondary brand colors",
          "Add your sponsor's logo with clickable website link",
          "Professional gradient headers with your colors",
          "Instant updates across all allocation pages",
          "Acknowledge sponsors on every shared schedule"
        ]}
        upgradeUrl="/pricing"
      />

      {/* Trial Expired Modal */}
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
      />
    </>
  );
};

export default ClubProfile;
